"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { ArTable } from "@/components/ui/ar-table";
import {
  ContextMenuItem,
  ContextMenuSeparator,
} from "@/components/ui/context-menu";
import { Icon } from "@/components/ui/icon";
import { TableRowContextMenu } from "@/components/ui/table-row-context-menu";
import {
  CompanyCell,
  JobTitleCell,
  SocialsCell,
  StageCell,
  fromPipelineCandidate,
} from "@/features/candidate";
import { useExportBusy } from "@/features/exports/tracker";
import { usePipelineJob } from "@/features/jobs/context/pipeline-job-context";
import { usePipelineTableStore } from "@/features/jobs/store/pipeline/table";
import type { PipelineCandidate, PipelineStage } from "@/lib/api/types";
import { useQueryClient } from "@tanstack/react-query";
import { contactsQueryKey } from "@/hooks/use-contact-reveal";
import { HeaderLabel, AtsHeaderLabel } from "./column-defs";
import {
  PipelineSelectionProvider,
  usePipelineSelection,
} from "./selection-context";
import {
  AddedAtCell,
  EducationCell,
  EmailCell,
  InStageCell,
  LocationCell,
  NameCell,
  SelectCell,
  SelectHeader,
  SequencesCell,
  SyncCell,
} from "./cells";
import { usePipelineRowContextActions } from "./use-row-context-actions";
import { pipelineExportScope } from "../export-jobs";

interface PipelineTableProps {
  candidates: PipelineCandidate[];
  stages: PipelineStage[];
  totalCount: number;
  loading?: boolean;
  onCandidateClick: (candidate: PipelineCandidate) => void;
  onSelectAllCandidates?: () => void;
  /**
   * Whether the ATS "Sync" column is shown. False when the job has no ATS
   * link (and no dormant one) — the column is force-hidden for this instance
   * only, leaving the user's persisted column prefs untouched.
   */
  showSyncColumn?: boolean;
}

const PER_PAGE = 50;

const SORTABLE_COLS = [
  "name",
  "job_title",
  "company",
  "location",
  "stage",
  "in_stage",
  "sync",
  "added_at",
];

export function PipelineTable({
  candidates,
  stages,
  totalCount,
  loading,
  onCandidateClick,
  onSelectAllCandidates,
  showSyncColumn = true,
}: PipelineTableProps) {
  const queryClient = useQueryClient();

  // Prime contact cache from any already-loaded candidate contacts so the
  // review modal in EmailCell can skip a refetch when opening.
  useEffect(() => {
    for (const c of candidates) {
      if (!c.contacts || c.contacts.length === 0) continue;
      const key = contactsQueryKey(c.profile_id);
      if (queryClient.getQueryData(key)) continue;
      queryClient.setQueryData(key, {
        contacts: c.contacts.map((ct) => ({
          id: ct.id,
          source: ct.source ?? "pdl",
          type: ct.type,
          value: ct.value,
          email_type: ct.email_type ?? null,
          deliverability: ct.deliverability ?? null,
          carrier: ct.carrier ?? null,
          is_valid: ct.is_valid ?? null,
          line_type: ct.line_type ?? null,
          revealed_by_name: ct.revealed_by_name ?? null,
          feedback: ct.feedback ?? null,
          created_at: ct.created_at ?? "",
        })),
        credits_remaining: 0,
        already_revealed: c.already_revealed ?? false,
      });
    }
  }, [candidates, queryClient]);

  const stageMap = useMemo(() => {
    const map = new Map<string, PipelineStage>();
    for (const s of stages) map.set(s.id, s);
    return map;
  }, [stages]);

  // Gold-flash on rows that just appeared in this snapshot. Powered by
  // `useImportProgressSync` in the job layout, which invalidates the
  // pipeline query the moment a background import finishes — so the
  // flash fires right as the new candidate lands in the table, not on
  // the next user interaction. First snapshot (mount) and full-
  // replacement snapshots (stage filter change, page change) seed the
  // seen-set without flashing, so we only highlight real additions.
  const FLASH_RECENCY_MS = 30_000;
  const seenIdsRef = useRef<Set<string>>(new Set());
  const [flashIds, setFlashIds] = useState<Set<string>>(new Set());
  useEffect(() => {
    const current = new Set(candidates.map((c) => c.job_candidate_id));
    const seen = seenIdsRef.current;
    const overlap = [...current].filter((id) => seen.has(id)).length;

    if (seen.size === 0 || overlap === 0) {
      seenIdsRef.current = current;
      return;
    }

    const now = Date.now();
    const recentlyImported = candidates.filter((c) => {
      if (seen.has(c.job_candidate_id)) return false;
      if (!c.added_at) return false;
      return now - new Date(c.added_at).getTime() < FLASH_RECENCY_MS;
    });

    for (const id of current) seenIdsRef.current.add(id);
    if (recentlyImported.length === 0) return;

    const importedIds = recentlyImported.map((c) => c.job_candidate_id);
    setFlashIds((prev) => {
      const next = new Set(prev);
      for (const id of importedIds) next.add(id);
      return next;
    });
    const timer = setTimeout(() => {
      setFlashIds((prev) => {
        const next = new Set(prev);
        for (const id of importedIds) next.delete(id);
        return next;
      });
    }, 1400);
    return () => clearTimeout(timer);
  }, [candidates]);

  const { openExport, openEnroll, modals } = usePipelineRowContextActions();

  return (
    <>
      <PipelineSelectionProvider
        candidates={candidates}
        totalCount={totalCount}
        onCandidateClick={onCandidateClick}
        onSelectAllCandidates={onSelectAllCandidates}
      >
        <ArTable.Root
          store={usePipelineTableStore}
          hiddenColumns={showSyncColumn ? undefined : ["sync"]}
        >
          <ArTable.Table>
            <ArTable.Head sortable={SORTABLE_COLS}>
              <SelectHeader />
              <HeaderLabel icon="IconPeople" label="Name" />
              <HeaderLabel icon="IconContacts" label="Contact" />
              <HeaderLabel icon="IconWorld" label="Socials" />
              <HeaderLabel icon="IconPlan" label="Stage" />
              <HeaderLabel icon="IconClock" label="In Stage" />
              <AtsHeaderLabel />
              <HeaderLabel icon="IconPaperPlane" label="Sequences" />
              <HeaderLabel icon="IconMedal" label="Job Title" />
              <HeaderLabel icon="IconBuildings" label="Company" />
              <HeaderLabel icon="IconMapPin" label="Location" />
              <HeaderLabel icon="IconGraduateCap" label="Education" />
              <HeaderLabel icon="IconPlusSmall" label="Added" />
            </ArTable.Head>

            <ArTable.Body
              data={candidates}
              loading={loading}
              emptyMessage="No candidates found in this stage."
            >
              {(candidate, index) => (
                <PipelineTableRow
                  key={candidate.job_candidate_id}
                  candidate={candidate}
                  index={index}
                  stageMap={stageMap}
                  flashing={flashIds.has(candidate.job_candidate_id)}
                  onExport={() => openExport(candidate)}
                  onEnroll={() => openEnroll(candidate)}
                />
              )}
            </ArTable.Body>
          </ArTable.Table>

          <ArTable.Scrollbar />

          <ArTable.Footer
            totalCount={totalCount}
            perPage={PER_PAGE}
            disabled={loading}
          >
            <span className="text-sm font-medium text-contrast/60">
              {totalCount} {totalCount === 1 ? "profile" : "profiles"}
            </span>
          </ArTable.Footer>
        </ArTable.Root>
      </PipelineSelectionProvider>
      {modals}
    </>
  );
}

function PipelineTableRow({
  candidate,
  index,
  stageMap,
  flashing,
  onExport,
  onEnroll,
}: {
  candidate: PipelineCandidate;
  index: number;
  stageMap: Map<string, PipelineStage>;
  flashing?: boolean;
  onExport: () => void;
  onEnroll: () => void;
}) {
  const { isSelected, onRowClick } = usePipelineSelection();
  const selected = isSelected(candidate.job_candidate_id);
  const row = fromPipelineCandidate(candidate);
  const stage = stageMap.get(candidate.stage_id ?? "");

  return (
    <TableRowContextMenu
      content={
        <>
          <ExportProfileMenuItem
            profileId={candidate.profile_id}
            onSelect={onExport}
          />
          <ContextMenuSeparator />
          <ContextMenuItem onSelect={onEnroll}>
            <Icon name="IconPaperPlane" size={14} />
            Add to Sequence
          </ContextMenuItem>
        </>
      }
    >
      <ArTable.Row
        onClick={(e) => onRowClick(candidate, index, e)}
        data-state={selected ? "selected" : undefined}
        className={flashing ? "pipeline-row-flash" : undefined}
      >
        <SelectCell candidate={candidate} index={index} />
        <NameCell candidate={candidate} />
        <EmailCell candidate={candidate} />
        <SocialsCell row={row} />
        <StageCell
          name={stage ? (candidate.stage_name ?? stage.name) : null}
          color={stage?.color}
        />
        <InStageCell candidate={candidate} />
        <SyncCell candidate={candidate} />
        <SequencesCell candidate={candidate} />
        <JobTitleCell row={row} />
        <CompanyCell row={row} />
        <LocationCell candidate={candidate} />
        <EducationCell candidate={candidate} />
        <AddedAtCell candidate={candidate} />
      </ArTable.Row>
    </TableRowContextMenu>
  );
}

function ExportProfileMenuItem({
  profileId,
  onSelect,
}: {
  profileId: string;
  onSelect: () => void;
}) {
  const { job } = usePipelineJob();
  const exporting = useExportBusy(
    pipelineExportScope.profile(job.id, profileId),
  );
  return (
    <ContextMenuItem onSelect={onSelect} disabled={exporting}>
      <Icon
        name="IconFileDownload"
        size={14}
        className="text-heading opacity-60"
      />
      Export Profile
    </ContextMenuItem>
  );
}
